/**
 * B2B Invoice Form JavaScript
 * ===========================
 * Handles form interactions, validation, and cart attribute updates.
 * 
 * This script:
 * 1. Shows/hides B2B fields based on toggle
 * 2. Validates required fields
 * 3. Updates cart attributes via AJAX
 * 4. Persists data to customer metafields (if logged in)
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    selectors: {
      form: '#b2b-invoice-section',
      toggle: '#is-b2b-customer',
      fieldsContainer: '#b2b-fields-container',
      requiredFields: '[data-required="true"]',
      companyName: '#b2b-company-name',
      vatNumber: '#b2b-vat-number',
      fiscalCode: '#b2b-fiscal-code',
      companyRegistry: '#b2b-company-registry',
      sdiCode: '#b2b-sdi-code',
      pecEmail: '#b2b-pec-email',
      invoiceNotes: '#b2b-invoice-notes',
      checkoutButton: '[name="checkout"], .cart__checkout-button, #checkout, .checkout-button'
    },
    debounceDelay: 500,
    animationDuration: 300
  };

  // State
  let debounceTimer = null;
  let isUpdating = false;

  /**
   * Initialize the B2B form
   */
  function init() {
    const form = document.querySelector(CONFIG.selectors.form);
    if (!form) return;

    bindEvents();
    loadSavedData();
    updateFieldsVisibility();
  }

  /**
   * Bind all event listeners
   */
  function bindEvents() {
    // Toggle checkbox
    const toggle = document.querySelector(CONFIG.selectors.toggle);
    if (toggle) {
      toggle.addEventListener('change', handleToggleChange);
    }

    // Input fields - debounced update
    const fields = document.querySelectorAll(`${CONFIG.selectors.form} input, ${CONFIG.selectors.form} textarea`);
    fields.forEach(field => {
      field.addEventListener('input', debounceUpdateCart);
      field.addEventListener('blur', validateField);
    });

    // Checkout button - validate before proceeding
    const checkoutButtons = document.querySelectorAll(CONFIG.selectors.checkoutButton);
    checkoutButtons.forEach(button => {
      button.addEventListener('click', handleCheckoutClick);
    });

    // Form submission (if wrapped in a form)
    const form = document.querySelector(CONFIG.selectors.form);
    if (form.closest('form')) {
      form.closest('form').addEventListener('submit', handleFormSubmit);
    }
  }

  /**
   * Handle toggle checkbox change
   */
  function handleToggleChange(event) {
    const isB2B = event.target.checked;
    updateFieldsVisibility(isB2B);
    
    if (!isB2B) {
      clearB2BFields();
    }
    
    updateCart();
  }

  /**
   * Show/hide B2B fields with animation
   */
  function updateFieldsVisibility(isB2B = null) {
    const toggle = document.querySelector(CONFIG.selectors.toggle);
    const container = document.querySelector(CONFIG.selectors.fieldsContainer);
    
    if (!container) return;
    
    const shouldShow = isB2B !== null ? isB2B : toggle?.checked;
    
    if (shouldShow) {
      container.style.display = 'block';
      container.style.opacity = '0';
      container.style.maxHeight = '0';
      
      requestAnimationFrame(() => {
        container.style.transition = `opacity ${CONFIG.animationDuration}ms ease, max-height ${CONFIG.animationDuration}ms ease`;
        container.style.opacity = '1';
        container.style.maxHeight = '1000px';
      });
    } else {
      container.style.transition = `opacity ${CONFIG.animationDuration}ms ease, max-height ${CONFIG.animationDuration}ms ease`;
      container.style.opacity = '0';
      container.style.maxHeight = '0';
      
      setTimeout(() => {
        container.style.display = 'none';
      }, CONFIG.animationDuration);
    }
  }

  /**
   * Clear all B2B field values
   */
  function clearB2BFields() {
    const fields = [
      CONFIG.selectors.companyName,
      CONFIG.selectors.vatNumber,
      CONFIG.selectors.fiscalCode,
      CONFIG.selectors.companyRegistry,
      CONFIG.selectors.sdiCode,
      CONFIG.selectors.pecEmail,
      CONFIG.selectors.invoiceNotes
    ];

    fields.forEach(selector => {
      const field = document.querySelector(selector);
      if (field) {
        field.value = '';
        clearFieldError(field);
      }
    });
  }

  /**
   * Debounced cart update
   */
  function debounceUpdateCart() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateCart, CONFIG.debounceDelay);
  }

  /**
   * Update cart attributes via AJAX
   */
  async function updateCart() {
    if (isUpdating) return;
    
    const toggle = document.querySelector(CONFIG.selectors.toggle);
    const isB2B = toggle?.checked || false;

    const attributes = {
      'is_b2b_customer': isB2B ? 'true' : 'false'
    };

    if (isB2B) {
      const fieldMappings = {
        [CONFIG.selectors.companyName]: 'b2b_company_name',
        [CONFIG.selectors.vatNumber]: 'b2b_vat_number',
        [CONFIG.selectors.fiscalCode]: 'b2b_fiscal_code',
        [CONFIG.selectors.companyRegistry]: 'b2b_company_registry',
        [CONFIG.selectors.sdiCode]: 'b2b_sdi_code',
        [CONFIG.selectors.pecEmail]: 'b2b_pec_email',
        [CONFIG.selectors.invoiceNotes]: 'b2b_invoice_notes'
      };

      Object.entries(fieldMappings).forEach(([selector, attributeName]) => {
        const field = document.querySelector(selector);
        if (field) {
          attributes[attributeName] = field.value.trim();
        }
      });
    }

    isUpdating = true;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      console.log('B2B invoice data saved to cart');
    } catch (error) {
      console.error('Error updating cart attributes:', error);
    } finally {
      isUpdating = false;
    }
  }

  /**
   * Validate a single field
   */
  function validateField(event) {
    const field = event.target;
    const isRequired = field.dataset.required === 'true';
    const toggle = document.querySelector(CONFIG.selectors.toggle);
    const isB2B = toggle?.checked || false;

    if (!isB2B) {
      clearFieldError(field);
      return true;
    }

    if (isRequired && !field.value.trim()) {
      showFieldError(field, 'This field is required');
      return false;
    }

    // VAT number format validation (basic)
    if (field.id === 'b2b-vat-number' && field.value.trim()) {
      const vatPattern = /^[A-Z]{2}[0-9A-Z]{2,12}$/i;
      if (!vatPattern.test(field.value.trim())) {
        showFieldError(field, 'Please enter a valid VAT number (e.g., IT12345678901)');
        return false;
      }
    }

    // SDI code validation
    if (field.id === 'b2b-sdi-code' && field.value.trim()) {
      const sdiPattern = /^[A-Z0-9]{7}$/i;
      if (!sdiPattern.test(field.value.trim())) {
        showFieldError(field, 'SDI code must be exactly 7 alphanumeric characters');
        return false;
      }
    }

    // PEC email validation
    if (field.id === 'b2b-pec-email' && field.value.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(field.value.trim())) {
        showFieldError(field, 'Please enter a valid email address');
        return false;
      }
    }

    clearFieldError(field);
    return true;
  }

  /**
   * Validate all required fields
   */
  function validateAllFields() {
    const toggle = document.querySelector(CONFIG.selectors.toggle);
    const isB2B = toggle?.checked || false;

    if (!isB2B) return true;

    const requiredFields = document.querySelectorAll(`${CONFIG.selectors.form} ${CONFIG.selectors.requiredFields}`);
    let isValid = true;

    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        showFieldError(field, 'This field is required');
        isValid = false;
      }
    });

    return isValid;
  }

  /**
   * Show error message for a field
   */
  function showFieldError(field, message) {
    field.classList.add('b2b-field-input--error');
    
    const errorElement = document.querySelector(`#error-${field.id.replace('b2b-', '')}`);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
  }

  /**
   * Clear error message for a field
   */
  function clearFieldError(field) {
    field.classList.remove('b2b-field-input--error');
    
    const errorElement = document.querySelector(`#error-${field.id.replace('b2b-', '')}`);
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    }
  }

  /**
   * Handle checkout button click
   */
  function handleCheckoutClick(event) {
    const toggle = document.querySelector(CONFIG.selectors.toggle);
    const isB2B = toggle?.checked || false;

    if (isB2B && !validateAllFields()) {
      event.preventDefault();
      event.stopPropagation();
      
      // Scroll to first error
      const firstError = document.querySelector('.b2b-field-input--error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstError.focus();
      }
      
      return false;
    }

    return true;
  }

  /**
   * Handle form submission
   */
  function handleFormSubmit(event) {
    const toggle = document.querySelector(CONFIG.selectors.toggle);
    const isB2B = toggle?.checked || false;

    if (isB2B && !validateAllFields()) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  /**
   * Load saved data from localStorage or customer metafields
   */
  function loadSavedData() {
    // Try to load from localStorage first
    const savedData = localStorage.getItem('b2b_invoice_data');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        populateFields(data);
      } catch (e) {
        console.error('Error loading saved B2B data:', e);
      }
    }
  }

  /**
   * Populate form fields with data
   */
  function populateFields(data) {
    const fieldMappings = {
      'b2b_company_name': CONFIG.selectors.companyName,
      'b2b_vat_number': CONFIG.selectors.vatNumber,
      'b2b_fiscal_code': CONFIG.selectors.fiscalCode,
      'b2b_company_registry': CONFIG.selectors.companyRegistry,
      'b2b_sdi_code': CONFIG.selectors.sdiCode,
      'b2b_pec_email': CONFIG.selectors.pecEmail,
      'b2b_invoice_notes': CONFIG.selectors.invoiceNotes
    };

    Object.entries(fieldMappings).forEach(([dataKey, selector]) => {
      const field = document.querySelector(selector);
      if (field && data[dataKey] && !field.value) {
        field.value = data[dataKey];
      }
    });
  }

  /**
   * Save data to localStorage
   */
  function saveToLocalStorage() {
    const data = {};
    const fields = document.querySelectorAll(`${CONFIG.selectors.form} input, ${CONFIG.selectors.form} textarea`);
    
    fields.forEach(field => {
      if (field.name && field.name.includes('attributes[')) {
        const key = field.name.replace('attributes[', '').replace(']', '');
        data[key] = field.value;
      }
    });

    localStorage.setItem('b2b_invoice_data', JSON.stringify(data));
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Save to localStorage before page unload
  window.addEventListener('beforeunload', saveToLocalStorage);

})();
